import React, { useMemo, useState } from 'react'
import { Button } from '@/components/ui/Button'

export type QuizQuestion = {
  id: string
  type: 'multiple_choice' | 'multi_select' | 'true_false'
  prompt: string
  options?: string[]
  correctAnswers: number[]
  explanation?: string
}

export type QuizPayload = {
  quizId: string
  lessonId?: string
  title?: string
  difficulty?: 'easy' | 'medium' | 'hard'
  objectives?: string[]
  questions: QuizQuestion[]
  metadata?: Record<string, unknown>
}

interface QuizModalProps {
  isOpen: boolean
  quiz: QuizPayload | null
  onClose: () => void
  onSubmit: (result: {
    quizId: string
    lessonId?: string
    score: number
    total: number
    perQuestion: Array<{ questionId: string; correct: boolean; userAnswers: number[] }>
    weakObjectives?: string[]
    answers: number[][]
  }) => void
}

export default function QuizModal({ isOpen, quiz, onClose, onSubmit }: QuizModalProps) {
  const [answers, setAnswers] = useState<Record<string, number[]>>({})
  const [submitted, setSubmitted] = useState(false)

  const total = quiz?.questions.length || 0

  const grading = useMemo(() => {
    if (!quiz) return { score: 0, perQuestion: [] as Array<{ questionId: string; correct: boolean; userAnswers: number[] }>, weakObjectives: [] as string[] }
    const perQuestion = quiz.questions.map(q => {
      const user = answers[q.id] || []
      const isCorrect = arrayEqual(new Set(user), new Set(q.correctAnswers))
      return { questionId: q.id, correct: isCorrect, userAnswers: user }
    })
    const score = perQuestion.filter(p => p.correct).length
    return { score, perQuestion, weakObjectives: deriveWeakObjectives(quiz, perQuestion) }
  }, [answers, quiz])

  if (!isOpen || !quiz) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 text-white">{quiz.title || 'Quick Quiz'}</h2>
            {quiz.objectives && quiz.objectives.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">Objectives: {quiz.objectives.join(', ')}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-1">
          {quiz.questions.map((q, idx) => (
            <div key={q.id} className="border border-gray-200 rounded-xl p-4">
              <div className="font-medium text-gray-900 mb-2">{idx + 1}. {q.prompt}</div>
              {renderQuestion(q, answers[q.id] || [], sel => setAnswers(prev => ({ ...prev, [q.id]: sel })))}
              {submitted && q.explanation && (
                <div className="mt-3 text-sm text-gray-700">
                  <span className="font-semibold">Explanation:</span> {q.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {submitted ? (
            <div className="text-sm text-gray-800">
              Score: <span className="font-semibold">{grading.score}</span> / {total}
            </div>
          ) : <div />}

          {!submitted ? (
            <Button
              onClick={() => {
                setSubmitted(true)
                onSubmit({
                  quizId: quiz.quizId,
                  lessonId: quiz.lessonId,
                  score: grading.score,
                  total,
                  perQuestion: grading.perQuestion,
                  weakObjectives: grading.weakObjectives,
                  answers: quiz.questions.map(q => answers[q.id] || [])
                })
              }}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              Submit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={onClose} variant="ghost" size="sm">Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function renderQuestion(q: QuizQuestion, selected: number[], onChange: (sel: number[]) => void) {
  if (q.type === 'true_false') {
    const opts = ['True', 'False']
    return (
      <div className="space-y-2">
        {opts.map((opt, i) => (
          <label key={i} className="flex items-center gap-2 text-gray-800">
            <input
              type="radio"
              name={q.id}
              checked={selected[0] === i}
              onChange={() => onChange([i])}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    )
  }

  if (q.type === 'multi_select') {
    return (
      <div className="space-y-2">
        {(q.options || []).map((opt, i) => (
          <label key={i} className="flex items-center gap-2 text-gray-800">
            <input
              type="checkbox"
              checked={selected.includes(i)}
              onChange={(e) => {
                const next = new Set(selected)
                if (e.target.checked) next.add(i); else next.delete(i)
                onChange([...next].sort((a,b)=>a-b))
              }}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    )
  }

  // multiple_choice
  return (
    <div className="space-y-2">
      {(q.options || []).map((opt, i) => (
        <label key={i} className="flex items-center gap-2 text-gray-800">
          <input
            type="radio"
            name={q.id}
            checked={selected[0] === i}
            onChange={() => onChange([i])}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  )
}

function arrayEqual(a: Set<number>, b: Set<number>) {
  if (a.size !== b.size) return false
  for (const v of a) if (!b.has(v)) return false
  return true
}

function deriveWeakObjectives(quiz: QuizPayload, perQuestion: Array<{ questionId: string; correct: boolean }>) {
  if (!quiz.objectives || quiz.objectives.length === 0) return []
  const incorrectCount = perQuestion.filter(p => !p.correct).length
  if (incorrectCount === 0) return []
  // Simple heuristic: surface all objectives when user misses any
  return quiz.objectives.slice(0, 3)
}


